import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VaccinationsData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { DisplayService } from '@services/display.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { BehaviorSubject, take } from 'rxjs';

@Component({
  selector: 'gc-vaccinations',
  templateUrl: './vaccinations.component.html',
  styleUrls: ['./vaccinations.component.scss'],
  standalone: false,
})
export class VaccinationsComponent implements OnInit, OnChanges {
  @Input() userId: number;
  @Input() vaccinationsData: VaccinationsData[] = [];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  vaccinationsForm: FormGroup;
  historyListForm: FormGroup;
  panelOpenState = false;
  isMobile$: BehaviorSubject<boolean>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
    private readonly displayService: DisplayService,
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.isMobile$ = this.displayService.isMobile$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vaccinationsData'] && !changes['vaccinationsData'].firstChange) {
      this.buildForms();
    }
  }

  buildForms(): void {
    this.vaccinationsForm = this.initRow({ date: null, vaccination: '' });

    this.vaccinationsData = this.vaccinationsData ? this.vaccinationsData : [];

    this.historyListForm = this.fb.group({
      rowList: this.fb.array(this.vaccinationsData.map((vaccination) => this.initRow(vaccination))),
    });
  }

  checkDateValid(control: AbstractControl): boolean {
    const parentControl = control.parent.controls;
    const controlName = Object.keys(parentControl).find((name) => parentControl[name] === control);
    if (control.touched && control.value === '') {
      this.vaccinationsForm.get(controlName).setErrors({ invalid: true });
      return true;
    }
    return false;
  }

  initRow(vaccination: VaccinationsData): FormGroup {
    return this.fb.group({
      date: [vaccination.date || '', Validators.required],
      vaccination: [vaccination.vaccination || '', Validators.required],
    });
  }

  get rowListControl(): FormArray {
    return this.historyListForm.get('rowList') as FormArray;
  }

  addToList() {
    if (this.vaccinationsForm.valid) {
      this.rowListControl.push(this.initRow(this.vaccinationsForm.value));
      this.vaccinationsForm.reset();
    }
  }

  deleteRow(index: number) {
    this.rowListControl.removeAt(index);
  }

  saveToRequest() {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'vaccinations', this.rowListControl.value)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'vaccinations',
            values: this.rowListControl.value,
          });
          this.alertService.snackbar('vaccinations_history_saved');
        }
      });
  }

  saveToProfile() {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'vaccinations', { user_id: this.userId, vaccinations: this.rowListControl.value })
      .subscribe(() => this.alertService.snackbar('vaccinations_saved'));
  }

  submit() {
    if (this.requestUuid) {
      this.saveToRequest();
    } else {
      this.saveToProfile();
    }
  }
}
